
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { TTab } from "@/models/tabs";
import { SMTasks } from "@/store";
import { TTask_Table, TActions, TTableHeader, TRule } from "@/models/tasks";
import { WARNING_POPUP_TYPE, ERROR_POPUP_TYPE } from "@/constants/popupTypes";
import StatusBadge from "@/components/StatusBadge/StatusBadge.vue";
import {
  COMPLETED_TASK_STATUS_LABEL,
  INACTIVE_TASKS_TABLE_ROW_TYPE,
  OVERDUE_TASK_STATUS_LABEL,
  STATUS_TASKS_TABLE_COLUMN_KEY,
  WARNING_TASKS_TABLE_ROW_TYPE,
} from "@/constants/tasks";

type ParamsKeyObject = {
  [key: string]: any;
};

const TYPES = {
  warning: WARNING_POPUP_TYPE,
  error: ERROR_POPUP_TYPE,
};

const SCROLL_BEHAVIOR = "smooth";

const TABLE_VERSION = 2;

const TABLE_RULES = [
  {
    name: INACTIVE_TASKS_TABLE_ROW_TYPE,
    column: STATUS_TASKS_TABLE_COLUMN_KEY,
    value: COMPLETED_TASK_STATUS_LABEL,
  },
  {
    name: WARNING_TASKS_TABLE_ROW_TYPE,
    column: STATUS_TASKS_TABLE_COLUMN_KEY,
    value: [OVERDUE_TASK_STATUS_LABEL, OVERDUE_TASK_STATUS_LABEL.toLowerCase()],
  },
];

@Component({
  components: {
    StatusBadge,
    Tabs: () => import("@/components/Tabs/Tabs.vue"),
  },
})
export default class TableTabs extends Vue {
  @Prop({ type: Array, required: true }) readonly headers!: TTableHeader[];
  @Prop({ type: Boolean, default: false }) readonly blocked!: boolean;
  @Prop({ type: Array, default: () => [] }) readonly tabValues!: string[];
  @Prop({ type: Object, default: null }) readonly tabNames!: ParamsKeyObject;
  @Prop({ type: Array, default: () => [] }) mainItems!: TTask_Table[];
  @Prop({ type: Array, default: () => [] }) sideItems!: TTask_Table[];
  @Prop({ type: Array, default: () => [] }) mainItemsInactive!: TTask_Table[];
  @Prop({ type: Array, default: () => [] }) sideItemsInactive!: TTask_Table[];
  @Prop({ type: Boolean, default: true }) readonly countSideItemsInactive?: boolean;
  @Prop({ type: Boolean, default: true }) readonly showDigit!: boolean;
  @Prop({ type: Object, default: null }) readonly actions!: TActions;
  @Prop({ type: Boolean, default: false }) readonly showActions!: boolean;
  @Prop({ type: Array, default: () => [] }) readonly rules?: TRule[];
  @Prop({ type: Boolean, default: true }) readonly showInactive?: boolean;
  @Prop({ type: String, default: "" }) readonly preSelectedTab?: string;

  types = TYPES;

  TABLE_VERSION = TABLE_VERSION;

  TABLE_RULES = TABLE_RULES;

  componentKey = 0;

  deletePopup = {
    isOpen: false,
    id: 0,
  };

  options = {
    infiniteScroll: true,
  };

  created() {
    SMTasks.unsetTabName();
  }

  @Watch("currentTabName", { immediate: true })
  currentTabNameChanged(value: string) {
    this.$emit("set-tab-name", value);
  }

  get taskTabNames() {
    if (this.tabNames) return this.tabNames;

    const tabNames: ParamsKeyObject = {};

    this.tabValues.forEach((value) => {
      tabNames[value] = value.replace("_", " ").toUpperCase();
    });

    return tabNames;
  }

  get currentTaskTabName() {
    if (!this.tabValues.includes(SMTasks.tabName)) return this.tabValues[0];

    return SMTasks.tabName || this.tabValues[0];
  }

  get isMainTab() {
    return this.mainTabName === this.currentTabName;
  }

  get hasActions() {
    return this.actions && Object.keys(this.actions).length;
  }

  get isActionsShown() {
    if (!this.hasActions) return false;

    if (this.showActions) return !!this.actions;

    return this.actions;
  }

  mainTabName = this.tabValues[0];

  currentTabName = this.preSelectedTab || this.currentTaskTabName;

  get loading() {
    return SMTasks.loading;
  }

  countSideItems() {
    return this.countSideItemsInactive && this.showInactive
      ? this.sideItems.length + this.sideItemsInactive.length
      : this.sideItems.length;
  }

  getTasksCount(name: string): number {
    return name == this.mainTabName ? this.mainItems.length : this.countSideItems();
  }

  setCurrentTabName(name) {
    this.currentTabName = name;
    this.componentKey += 1;
  }

  get tabs(): TTab[] {
    return this.tabValues.map((name) => ({
      label: (this.taskTabNames as any)[name]?.toUpperCase(),
      isActive: this.currentTabName === name,
      digit: this.showDigit ? this.getTasksCount(name) : 0,
      action: () => {
        this.setCurrentTabName(name);
      },
    }));
  }

  get labels() {
    return {
      allLabel: this.$t("table.allLabel"),
      placeholderLabel: this.$t("table.placeholderLabel"),
    };
  }

  get hasTasks() {
    return this.currentTabName == this.mainTabName
      ? this.mainItems.length
      : this.sideItems.length + this.sideItemsInactive.length;
  }

  get attributes() {
    const {
      headers,
      labels,
      mainItems,
      sideItems,
      mainItemsInactive,
      sideItemsInactive,
      currentTabName,
      mainTabName,
      blocked,
      options,
    } = this;

    const items = blocked ? [] : currentTabName === mainTabName ? mainItems : sideItems;

    const blockedItems =
      currentTabName === mainTabName
        ? [...mainItems, ...mainItemsInactive]
        : [...sideItems, ...sideItemsInactive];

    const notBlockedItems = currentTabName === mainTabName ? mainItemsInactive : sideItemsInactive;

    const inactiveItems = blocked ? blockedItems : notBlockedItems;

    return {
      headers,
      items,
      inactiveItems,
      labels,
      options,
      rules: this.TABLE_RULES,
    };
  }

  @Watch("attributes", { immediate: true, deep: true })
  attributesChanged() {
    this.componentKey += 1;
  }

  atRowClick(item: TTask_Table) {
    SMTasks.setTabName(this.currentTabName);

    this.$emit("row-click", item);
  }

  openDeletePopup(id: number) {
    if (this.blocked) return;

    this.deletePopup.isOpen = true;
    this.deletePopup.id = id;
  }

  closeDeletePopup() {
    this.deletePopup.isOpen = false;
  }

  async onDelete(id: number) {
    this.closeDeletePopup();

    if (this.actions.delete) await this.actions.delete(id);
  }

  onEdit(id: number) {
    if (this.actions.edit) this.actions.edit(id);
  }

  onDownload(item: TTask_Table) {
    if (this.actions.download) this.actions.download(item);
  }

  moveToTasksTableBlock() {
    const target = this.$el as HTMLElement;

    const view = this.$root.$el.querySelector(".main__view") as HTMLElement;

    if (!target || !view) return;

    view.scroll({
      top: target.offsetTop,
      behavior: SCROLL_BEHAVIOR,
    });
  }

  mounted() {
    this.$root.$on("move-to-tasks-table", this.moveToTasksTableBlock);

    if (this.$route.params.moveToTasksTable === "true") this.moveToTasksTableBlock();
  }

  getDateTimestampFromString(value) {
    const dates = value.split(" ");

    const dateByParts = dates.split("/");
    const date = new Date(dateByParts[2], dateByParts[1], dateByParts[0]);

    return date.getTime();
  }

  dateAltSort(a, b, asc, sortParam) {
    const dateA = this.getDateTimestampFromString(a[sortParam].toString());
    const dateB = this.getDateTimestampFromString(b[sortParam].toString());

    if (asc) {
      return dateA > dateB ? 1 : -1;
    } else {
      return dateA > dateB ? -1 : 1;
    }
  }

  sortUpdate(payload) {
    const { asc, sortParam, sortBy } = payload;

    if (sortBy === "dateAlt") {
      if (this.currentTabName === this.mainTabName) {
        this.mainItems.sort((a, b) => {
          return this.dateAltSort(a, b, asc, sortParam);
        });

        this.mainItemsInactive.sort((a, b) => {
          return this.dateAltSort(a, b, asc, sortParam);
        });
      } else {
        this.sideItems.sort((a, b) => {
          return this.dateAltSort(a, b, asc, sortParam);
        });

        this.sideItemsInactive.sort((a, b) => {
          return this.dateAltSort(a, b, asc, sortParam);
        });
      }
    }

    const curTabName = this.currentTaskTabName;
    const anotherTabName = this.tabValues.find((tabValue) => tabValue !== curTabName);

    this.currentTabName = anotherTabName;

    this.$nextTick(() => {
      this.$forceUpdate();

      this.currentTabName = anotherTabName;

      this.$forceUpdate();

      setTimeout(() => {
        this.currentTabName = curTabName;
      }, 300);
    });
  }
}
